import BaseService from "./services/BaseService";
import Axios from "axios";

export const SERVER_URI =
  process.env.VUE_APP_SERVER_URI || "http://localhost:3000";

export default class NSApi {
  static _instance = null;

  static get instance() {
    if (this._instance === null) this._instance = new NSApi(this._options);
    return this._instance;
  }

  // eslint-disable-next-line no-unused-vars
  constructor(options = {}) {
    this._axios = options.axios || Axios.create({ withCredentials: true });
    this.users = new BaseService(
      SERVER_URI + "/api/users",
      "user",
      "json",
      this._axios
    );
    this.projects = new BaseService(
      SERVER_URI + "/api/projects",
      "project",
      "json",
      this._axios
    );
  }

  async doLogin(email, password) {
    const r = await this._axios.post(
      SERVER_URI + "/services/auth",
      { username: email, password },
      {
        withCredentials: true,
      }
    );
    return r.data.user;
  }
  async getCurrentUser() {
    try {
      const r = await this._axios.get(SERVER_URI + "/services/auth", {
        withCredentials: true,
      });
      return r.data.user;
    } catch (ex) {
      return undefined;
    }
  }
  async updateCurrentUser(user) {
    const r = await this._axios.put(
      SERVER_URI + `/services/user`,
      { item: user },
      {
        withCredentials: true,
      }
    );

    return r.data.user;
  }
  async doLogout() {
    try {
      await this._axios.post(SERVER_URI + "/services/auth/logout", null, {
        withCredentials: true,
      });
      return true;
    } catch (error) {
      console.error("Logout failed:", error);
      return false;
    }
  }

  async getConfigProperty(propertyName) {
    const r = await this._axios.get(SERVER_URI + "/api/global_config", {
      params: { propertyName },
    });
    return r.data;
  }

  async setConfigProperty(propertyName, propertyValue) {
    const r = await this._axios.post(SERVER_URI + "/api/global_config", {
      propertyName,
      propertyValue,
    });
    return r.data;
  }

  async doPayment(products, patient_id) {
    const r = await this._axios.post(SERVER_URI + "/api/payment", {
      products,
      patient_id,
    });
    return r.data;
  }

  async doRefund(visit_id, patient_id) {
    const r = await this._axios.post(SERVER_URI + "/api/payment/refund", {
      visit_id,
      patient_id,
    });
    return r.data;
  }

  async doB2C_Course(
    customer,
    category,
    stage,
    customCourseName,
    bitrixManagerId
  ) {
    const r = await this._axios.post(SERVER_URI + "/api/orders", {
      order_date: new Date().toISOString(),
      order_source: "M",
      order_status: "paid",
      total_amount: 0,
      stage: stage,
      customer: customer,
      custom_course_name: customCourseName,
      bitrix_manager_id: bitrixManagerId || null,
      order_items: [
        {
          // Позиция заказа — это курс. Раньше поле называлось product_id,
          // хотя всегда содержало id категории.
          category_id: category,
          price: 0,
        },
      ],
    });
    return r.data;
  }

  async getCustomers() {
    const r = await this._axios.get(SERVER_URI + "/v1/customers");
    return r.data;
  }

  async getOrderPrice(category_id) {
    const r = await this._axios.get(SERVER_URI + "/api/patients/order", {
      params: { category_id },
    });
    return r.data;
  }

  async getPatientEmailStatus(patient_id) {
    const r = await this._axios.get(SERVER_URI + "/v1/patients/has-email", {
      params: { patient_id },
    });
    return r.data;
  }

  async doB2B_Course(
    category_id,
    patient_id,
    stage = null,
    custom_course_name = null
  ) {
    const requestData = {
      category_id: category_id,
      patient_id: patient_id,
    };
    if (stage) {
      requestData.stage = stage;
    }
    if (custom_course_name) {
      requestData.custom_course_name = custom_course_name;
    }
    const r = await this._axios.post(
      SERVER_URI + "/api/patients/order",
      requestData
    );
    return r.data;
  }

  async getAllCategories() {
    const r = await this._axios.get(SERVER_URI + "/v1/categories/staged");
    return r.data;
  }

  async createBalanceTopup(amount) {
    const r = await this._axios.post(SERVER_URI + "/api/balance-topup", {
      amount: amount,
    });
    return r.data;
  }

  async getNews(params = {}) {
    const r = await this._axios.get(SERVER_URI + "/api/news", { params });
    return r.data;
  }

  async getNewsById(id) {
    const r = await this._axios.get(SERVER_URI + "/api/news/" + id);
    return r.data;
  }

  async parseNewsMarkdown(file) {
    const formData = new FormData();
    formData.append("file", file);
    const r = await this._axios.post(
      SERVER_URI + "/api/news/parse-markdown",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return r.data;
  }

  async generateNewsFromSource(sourceText) {
    const r = await this._axios.post(
      SERVER_URI + "/api/news/generate-from-text",
      { sourceText },
      { timeout: 180000 }
    );
    return r.data;
  }

  async generateNewsSeo({ title, content }) {
    const r = await this._axios.post(
      SERVER_URI + "/api/news/generate-seo",
      { title, content },
      { timeout: 180000 }
    );
    return r.data;
  }

  async createNews(newsData) {
    const formData = new FormData();
    formData.append("title", newsData.title || "");
    formData.append("active", newsData.active !== false);
    formData.append(
      "scope",
      newsData.scope === "internal" ? "internal" : "site"
    );
    if (newsData.site)
      formData.append(
        "site",
        newsData.site === "neurobica" ? "neurobica" : "upmind"
      );
    if (newsData.type) formData.append("type", newsData.type);
    if (newsData.readingTime != null)
      formData.append("readingTime", newsData.readingTime);
    if (newsData.views != null)
      formData.append("views", String(newsData.views));
    if (newsData.tableOfContents && Array.isArray(newsData.tableOfContents)) {
      formData.append(
        "tableOfContents",
        JSON.stringify(newsData.tableOfContents)
      );
    }
    if (newsData.content !== undefined) {
      if (Array.isArray(newsData.content)) {
        formData.append("content", JSON.stringify(newsData.content));
      } else {
        formData.append(
          "content",
          JSON.stringify([{ title: "", text: String(newsData.content) }])
        );
      }
    }
    if (newsData.videoUrl) formData.append("videoUrl", newsData.videoUrl);
    if (
      newsData.poster &&
      typeof newsData.poster === "object" &&
      newsData.poster instanceof File
    ) {
      formData.append("poster", newsData.poster);
    } else if (newsData.poster && typeof newsData.poster === "string") {
      formData.append("poster", newsData.poster);
    }
    if (newsData.image) formData.append("image", newsData.image);
    if (newsData.slug != null) formData.append("slug", newsData.slug);
    if (newsData.seoTitle != null)
      formData.append("seoTitle", newsData.seoTitle);
    if (newsData.seoDescription != null)
      formData.append("seoDescription", newsData.seoDescription);
    if (newsData.imageAlt != null)
      formData.append("imageAlt", newsData.imageAlt);
    if (newsData.expiresAt != null)
      formData.append("expiresAt", newsData.expiresAt);
    const r = await this._axios.post(SERVER_URI + "/api/news", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
  }

  async updateNews(id, newsData) {
    const formData = new FormData();
    if (newsData.title !== undefined) formData.append("title", newsData.title);
    if (newsData.type !== undefined) formData.append("type", newsData.type);
    if (newsData.readingTime !== undefined)
      formData.append("readingTime", newsData.readingTime);
    if (newsData.views !== undefined)
      formData.append("views", String(newsData.views));
    if (newsData.tableOfContents !== undefined) {
      formData.append(
        "tableOfContents",
        Array.isArray(newsData.tableOfContents)
          ? JSON.stringify(newsData.tableOfContents)
          : newsData.tableOfContents
      );
    }
    if (newsData.content !== undefined) {
      formData.append(
        "content",
        Array.isArray(newsData.content)
          ? JSON.stringify(newsData.content)
          : newsData.content
      );
    }
    if (newsData.videoUrl !== undefined)
      formData.append("videoUrl", newsData.videoUrl);
    if (
      newsData.poster &&
      typeof newsData.poster === "object" &&
      newsData.poster instanceof File
    ) {
      formData.append("poster", newsData.poster);
    } else if (newsData.poster !== undefined && newsData.poster !== null) {
      formData.append("poster", newsData.poster);
    }
    if (newsData.active !== undefined)
      formData.append("active", newsData.active);
    if (newsData.scope !== undefined)
      formData.append(
        "scope",
        newsData.scope === "internal" ? "internal" : "site"
      );
    if (newsData.site !== undefined)
      formData.append(
        "site",
        newsData.site === "neurobica" ? "neurobica" : "upmind"
      );
    if (newsData.image) formData.append("image", newsData.image);
    if (newsData.slug !== undefined)
      formData.append("slug", newsData.slug || "");
    if (newsData.seoTitle !== undefined)
      formData.append("seoTitle", newsData.seoTitle || "");
    if (newsData.seoDescription !== undefined)
      formData.append("seoDescription", newsData.seoDescription || "");
    if (newsData.imageAlt !== undefined)
      formData.append("imageAlt", newsData.imageAlt || "");
    if (newsData.deleteImage) formData.append("deleteImage", "true");
    if (newsData.expiresAt !== undefined)
      formData.append("expiresAt", newsData.expiresAt || "");
    const r = await this._axios.put(SERVER_URI + "/api/news/" + id, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
  }

  async deleteNews(id) {
    const r = await this._axios.delete(SERVER_URI + "/api/news/" + id);
    return r.data;
  }

  async getViewedNewsIds() {
    const r = await this._axios.get(SERVER_URI + "/api/news/viewed");
    return r.data;
  }

  async markNewsViewed(id) {
    const r = await this._axios.post(
      SERVER_URI + "/api/news/" + id + "/viewed"
    );
    return r.data;
  }

  async requestPasswordReset(email) {
    return this._axios.post(SERVER_URI + "/api/auth/request-password-reset", {
      email,
    });
  }

  async resetPassword(email, token, password) {
    return this._axios.post(SERVER_URI + "/api/auth/reset-password", {
      email,
      token,
      password,
    });
  }

  async changePassword(currentPassword, newPassword) {
    return this._axios.post(SERVER_URI + "/api/services/user/change-password", {
      currentPassword,
      newPassword,
    });
  }

  // Dashboards API
  async getDashboards() {
    const r = await this._axios.get(SERVER_URI + "/api/dashboards");
    return r.data;
  }

  async createDashboard(name) {
    const r = await this._axios.post(SERVER_URI + "/api/dashboards", { name });
    return r.data;
  }

  async updateDashboard(id, data) {
    const r = await this._axios.put(SERVER_URI + `/api/dashboards/${id}`, data);
    return r.data;
  }

  async deleteDashboard(id) {
    const r = await this._axios.delete(SERVER_URI + `/api/dashboards/${id}`);
    return r.data;
  }

  async getCallsStatistics(params = {}) {
    const r = await this._axios.get(
      SERVER_URI + "/api/dashboards/widgets/calls-statistics",
      { params }
    );
    return r.data;
  }

  // Materials API
  async getMaterials(params = {}) {
    const r = await this._axios.get(SERVER_URI + "/api/materials", { params });
    return r.data;
  }

  async getMaterialById(id) {
    const r = await this._axios.get(SERVER_URI + "/api/materials/" + id);
    return r.data;
  }

  async createMaterial(data) {
    const formData = new FormData();
    formData.append("type", data.type);
    formData.append("audience", data.audience || "adults");
    formData.append("title", data.title || "");
    if (data.description != null)
      formData.append("description", data.description);
    if (data.link) formData.append("link", data.link);
    formData.append("active", data.active !== false ? "true" : "false");
    if (data.sortOrder != null)
      formData.append("sortOrder", String(data.sortOrder));
    if (data.file instanceof File) formData.append("file", data.file);
    const r = await this._axios.post(SERVER_URI + "/api/materials", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
  }

  async updateMaterial(id, data) {
    const formData = new FormData();
    if (data.type !== undefined) formData.append("type", data.type);
    if (data.audience !== undefined) formData.append("audience", data.audience);
    if (data.title !== undefined) formData.append("title", data.title);
    if (data.description !== undefined)
      formData.append("description", data.description || "");
    if (data.link !== undefined) formData.append("link", data.link || "");
    if (data.active !== undefined)
      formData.append("active", data.active ? "true" : "false");
    if (data.sortOrder !== undefined)
      formData.append("sortOrder", String(data.sortOrder));
    if (data.file instanceof File) formData.append("file", data.file);
    if (data.deleteFile) formData.append("deleteFile", "true");
    const r = await this._axios.put(
      SERVER_URI + `/api/materials/${id}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return r.data;
  }

  async deleteMaterial(id) {
    const r = await this._axios.delete(SERVER_URI + `/api/materials/${id}`);
    return r.data;
  }

  async generateIndividualRecommendation(
    childName,
    childAge,
    description,
    filesData
  ) {
    const r = await this._axios.post(
      SERVER_URI + "/api/ai/individual-recommendation",
      {
        childName,
        childAge,
        description,
        files: filesData || [],
      }
    );
    return r.data;
  }
}
